/**
 * Per-member reservations panel (DNKADM-0003), hosted on MemberDetail's Reservations
 * tab. A convenience slice of the whole-tenant list: it fetches a window (today →
 * +30 days) via the same `GET /admin/reservations` endpoint and filters CLIENT-SIDE
 * to this member's `contactId`, then renders the design's reservation **cards** with
 * the shared Cancel / Reschedule dialogs.
 *
 * Re-skinned onto the MUI design (handoff §2c) — DNKADM-0013 U3, **presentation
 * only**: the data flow (`listReservationWindow` with a server-side `contactId`), the
 * cancel/reschedule write contracts, and the required-reason/refund semantics are
 * unchanged. Each card shows a date chip, the service/court + a status pill
 * (Upcoming / Completed / Cancelled), and the time; **Upcoming** rows expose
 * Reschedule (outlined) + Cancel (red outline), while completed/cancelled rows are
 * dimmed and non-actionable. The empty state uses `event_busy`.
 *
 * "Book on behalf" is rendered **disabled with a "Soon" pill** — DNKADM-0005 is
 * parked, so the affordance is shown but not built.
 *
 * Data notes (not fabricated): the per-booking credits figure is an **assumed**
 * additive field (DNKADM-0022). When dink-api carries it the card shows a credits
 * meta beside the time; when absent the card keeps its time-only meta (degrade).
 *
 * Scale caveat (documented): the endpoint is whole-tenant, so this fetches all of
 * the tenant's bookings in the window and filters in the browser. That is fine at
 * Sim Creek volume; if a tenant ever grows large, a contact-scoped server filter
 * would be the follow-up.
 *
 * **Service-location scoping (DNKADM-0032).** `/admin/reservations` now requires a
 * `serviceLocationId`, so the panel reads the app-level `useServiceLocation()`
 * (DNKADM-0033) and shows the member's bookings **at the currently-selected
 * location**. If the location context isn't ready it shows its own loading / error
 * state and never calls the endpoint unscoped. (A cross-location member view is a
 * possible later enhancement — out of scope here.) The UI is otherwise unchanged.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { COURT_GREEN, Icon, inkAlpha, MONO, resolveMaps, resolveTokens, type SemanticMaps } from '@/theme';
import { listReservationWindow, truncatedWindowMessage, type Reservation } from '@/api/reservations';
import { notifyConfirmationQueueChanged } from '@/features/confirmations/confirmationQueueEvents';
import { useServiceLocation } from '@/location/useServiceLocation';
import { useCalendarZone } from '@/location/useCalendarZone';
import { clockInZone } from '@/utils/clockTime';
import { roleLimitedMessage } from '@/components/accessCopy';
import { INVALID_WINDOW_MESSAGE, isInvalidWindowError } from './reservationsError';
import { serviceWithCoach } from './coachLabel';
import { paymentHowLabel } from './calendar/derive';
import { CancelReservationDialog, RescheduleDialog } from './ReservationActionDialogs';
import { CreateReservationDialog } from './CreateReservationDialog';
import { ScreenError } from '@/components/ScreenError';
import { formatInstantMs, instantMs } from '@/utils/instantFormat';

type LoadState =
  | { status: 'loading' }
  | { status: 'loaded'; data: Reservation[]; truncated: boolean }
  | { status: 'error'; error: unknown };

type ReservationPhase = 'upcoming' | 'completed' | 'cancelled';

const PHASE_LABEL: Record<ReservationPhase, string> = {
  upcoming: 'Upcoming',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/**
 * Status pill palette per phase (mirrors the handoff status tokens) — mode-aware
 * since DNKADM-0269, reusing the shared `STATUS` map's exact pairs (upcoming =
 * active, completed = paused, cancelled = cancelled) so light stays byte-identical.
 */
function phasePill(phase: ReservationPhase, maps: SemanticMaps): { label: string; fg: string; bg: string } {
  const status =
    phase === 'upcoming' ? maps.status.active : phase === 'completed' ? maps.status.paused : maps.status.cancelled;
  return { label: PHASE_LABEL[phase], fg: status.fg, bg: status.bg };
}

/** Fetch window for the member panel: now → +30 days, ISO-8601 with offset. */
function panelWindow(): { start: string; end: string } {
  const now = new Date();
  const end = new Date(now);
  end.setDate(end.getDate() + 30);
  return { start: now.toISOString(), end: end.toISOString() };
}

/**
 * Classify a reservation into the design's three phases. The booking `status` is
 * free GHL text; a cancelled-looking status wins, otherwise a start time in the past
 * is Completed and everything else is Upcoming (the default for this future window).
 */
function phaseOf(r: Reservation): ReservationPhase {
  if (r.status && /cancel/i.test(r.status)) return 'cancelled';
  if (r.startTime) {
    const start = new Date(r.startTime);
    if (!Number.isNaN(start.getTime()) && start.getTime() < Date.now()) return 'completed';
  }
  return 'upcoming';
}

/** Date-chip parts (uppercase month + day) from an ISO start instant. */
function dateChipParts(
  iso: string | null,
  timeZone: string | null = null,
): { mon: string; day: string } {
  const ms = instantMs(iso);
  if (ms === null) return { mon: '—', day: '' };
  // DNKADM-0437 V-25: the venue's calendar day, not the device's (an unusable zone name
  // degrades to the device zone), through the shared instant formatter (DNKADM-0476 F-05).
  return {
    mon: formatInstantMs(ms, timeZone, { month: 'short' }).toUpperCase(),
    day: formatInstantMs(ms, timeZone, { day: 'numeric' }),
  };
}

/**
 * Time range ("2:00 PM – 3:00 PM") from the ISO start/end; degrade gracefully. 12-hour
 * through the shared formatter (DNKADM-0386 U2). DNKADM-0437 V-25: rendered in the venue
 * zone like the rest of the console (the device zone only when the location has none).
 */
function timeRange(start: string | null, end: string | null, timeZone: string | null = null): string {
  const s = clockInZone(start, timeZone);
  const e = clockInZone(end, timeZone);
  if (s !== '—' && e !== '—') return `${s} – ${e}`;
  if (s !== '—') return s;
  return '—';
}

type ActiveModal = { bookingId: string; kind: 'cancel' | 'reschedule' } | null;

export interface MemberReservationsPanelProps {
  contactId: string;
  /** The member's identity as the hosting screen already renders it (DNKADM-0338 U1) — handed to
   *  Book on behalf so the dialog shows WHO, never a contact id. Null when the payload lacks it. */
  memberName: string | null;
  memberEmail: string | null;
}

export function MemberReservationsPanel({ contactId, memberName, memberEmail }: MemberReservationsPanelProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const location = useServiceLocation();
  const serviceLocationId = location.selectedId;
  // The selected location's zone, else the tenant's, else the device's (DNKADM-0478).
  const venueTimeZone = useCalendarZone();
  // DNKADM-0375: Book on behalf, Reschedule and Cancel are open to a STAFF session
  // (DNKAPI-0532), so this panel is the same for every operator.

  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [feedback, setFeedback] = useState<string | null>(null);
  const [active, setActive] = useState<ActiveModal>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    // Resolve a location before fetching — never call the endpoint unscoped.
    if (!serviceLocationId) return undefined;
    // An empty id must never reach the server — an absent `contactId` is the UNFILTERED list.
    if (!contactId) {
      setState({ status: 'loaded', data: [], truncated: false });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    const { start, end } = panelWindow();
    // DNKADM-0061: re-pointed to the owned v2 `/v2/admin/reservations`; the active
    // location (DNKADM-0051) is the REQUIRED `locationId`. The member's `contactId` is sent
    // to the server (DNKADM-0431) — a browser filter after a 20-row page dropped bookings.
    listReservationWindow({ locationId: serviceLocationId, start, end, contactId })
      .then(({ reservations, truncated }) => {
        if (!cancelled) setState({ status: 'loaded', data: reservations, truncated });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ status: 'error', error });
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, serviceLocationId]);

  useEffect(() => load(), [load]);

  function handleChanged(message: string) {
    setActive(null);
    setCreating(false);
    setFeedback(message);
    load();
  }

  const reservations = state.status === 'loaded' ? state.data : [];
  const activeReservation =
    active === null ? null : reservations.find((r) => r.bookingId === active.bookingId) ?? null;

  // Location gating (DNKADM-0032): resolve a location before fetching. While the
  // app-level chooser is loading, or fail-closed in error, show that state and never
  // call the endpoint unscoped.
  if (location.status === 'loading') {
    return (
      <Box>
        <Typography role="status" sx={{ color: t.text2 }}>
          Loading reservations…
        </Typography>
      </Box>
    );
  }
  if (location.status === 'error') {
    return (
      <Box>
        <ScreenError
          error={location.error}
          what="the service locations"
          onRetry={location.retry}
          retryMessage="The service locations couldn’t be loaded, so reservations can’t be shown. Try again."
        />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2
        }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Reservations</Typography>
        {/* "Book on behalf" — the live create-on-behalf flow (DNKADM-0061 U2). Enabled
            once a location is resolved (the booking's REQUIRED location). */}
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={() => setCreating(true)}
          disabled={!serviceLocationId}
        >
          Book on behalf
        </Button>
      </Stack>
      {feedback && (
        <Alert severity="success" role="status" sx={{ mb: 2 }}>
          {feedback}
        </Alert>
      )}
      {state.status === 'loading' && (
        <Typography role="status" sx={{ color: t.text2 }}>
          Loading reservations…
        </Typography>
      )}
      {state.status === 'error' && (
        <ScreenError
          error={state.error}
          what="the reservations"
          onRetry={() => void load()}
          forbiddenMessage={roleLimitedMessage('This screen', 'OWNER or ADMIN')}
          retryMessage="The reservations could not be loaded. Please try again."
          preferServerMessage
          rejectedMessage={isInvalidWindowError(state.error) ? INVALID_WINDOW_MESSAGE : undefined}
        />
      )}
      {state.status === 'loaded' && state.truncated && (
        <Alert severity="warning" role="status" sx={{ mb: 2 }} data-testid="member-reservations-truncated">
          {truncatedWindowMessage(state.data.length)}
        </Alert>
      )}
      {state.status === 'loaded' &&
        (state.data.length === 0 ? (
          <Paper elevation={1} sx={{ p: '52px 24px', textAlign: 'center' }}>
            <Icon name="event_busy" sx={{ fontSize: 46, color: inkAlpha(theme, 0.18) }} />
            <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
              No upcoming reservations for this member
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, maxWidth: 360, mx: 'auto' }}>
              Bookings for the next 30 days appear here. There’s nothing on the calendar right now.
            </Typography>
          </Paper>
        ) : (
          <Stack sx={{
            gap: 1.5
          }}>
            {state.data.map((r, i) => (
              <ReservationCard
                key={r.bookingId ?? `res-${i}`}
                reservation={r}
                timeZone={venueTimeZone}
                onCancel={() =>
                  r.bookingId && setActive({ bookingId: r.bookingId, kind: 'cancel' })
                }
                onReschedule={() =>
                  r.bookingId && setActive({ bookingId: r.bookingId, kind: 'reschedule' })
                }
              />
            ))}
          </Stack>
        ))}
      {activeReservation && active?.kind === 'cancel' && (
        <CancelReservationDialog
          open
          reservation={activeReservation}
          onClose={() => setActive(null)}
          onDone={(message) => {
            // Cancelling a still-pending request empties a queue slot — poke
            // the shared pending-count source so the nav badge/queue re-read
            // (DNKADM-0304 U3; the calendar's cancel path already does this).
            if (activeReservation.status === 'unconfirmed') notifyConfirmationQueueChanged();
            handleChanged(message);
          }}
        />
      )}
      {activeReservation && active?.kind === 'reschedule' && (
        <RescheduleDialog
          open
          reservation={activeReservation}
          onClose={() => setActive(null)}
          onDone={handleChanged}
        />
      )}
      {creating && serviceLocationId && (
        <CreateReservationDialog
          open
          locationId={serviceLocationId}
          presetContactId={contactId}
          presetContactName={memberName}
          presetContactEmail={memberEmail}
          onClose={() => setCreating(false)}
          onDone={handleChanged}
        />
      )}
    </Box>
  );
}

/**
 * One reservation card: a date chip, the service + status pill, and the time. Only
 * Upcoming rows expose Reschedule / Cancel; completed/cancelled rows are dimmed and
 * non-actionable (and rows missing a bookingId can't be acted on).
 */
function ReservationCard({
  reservation,
  timeZone,
  onCancel,
  onReschedule,
}: {
  reservation: Reservation;
  /** The selected location's IANA zone, or null (device zone). */
  timeZone: string | null;
  onCancel: () => void;
  onReschedule: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const maps = resolveMaps(theme);
  const phase = phaseOf(reservation);
  const pill = phasePill(phase, maps);
  const { mon, day } = dateChipParts(reservation.startTime, timeZone);
  const dimmed = phase !== 'upcoming';
  // DNKADM-0426: a checked-in / no-show booking is terminal for cancel and reschedule.
  const attended = reservation.status === 'showed' || reservation.status === 'no_show';
  const actionable = phase === 'upcoming' && !attended && reservation.bookingId !== null;
  // DNKADM-0454: how it was paid ("3 credits", "$65 card", "Pay later", "Comped"), never "0 credits".
  // DNKADM-0457: shown as a badge beside the status pill, from the one shared label.
  const payment = paymentHowLabel(reservation);

  return (
    <Paper
      elevation={1}
      sx={{ p: '14px 16px', opacity: dimmed ? 0.6 : 1 }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap"
        }}>
        {/* Date chip */}
        <Box
          sx={{
            width: 52,
            flex: 'none',
            textAlign: 'center',
            borderRadius: 2,
            py: 0.75,
            bgcolor: maps.greenTints.surfaceA,
            border: `1px solid ${t.lineFaint}`,
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.4px',
              color: COURT_GREEN,
            }}
          >
            {mon}
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 500, fontFamily: MONO }}>{day}</Typography>
        </Box>

        {/* Service + status + time */}
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap"
            }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              {serviceWithCoach(reservation) ?? 'Reservation'}
            </Typography>
            <Chip
              label={pill.label}
              size="small"
              sx={{ bgcolor: pill.bg, color: pill.fg, fontWeight: 500, height: 20, fontSize: 11 }}
            />
            {payment && (
              <Chip
                label={payment}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </Stack>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 0.75,
              mt: 0.5,
              color: t.text2
            }}>
            <Icon name="schedule" sx={{ fontSize: 15, color: inkAlpha(theme, 0.4) }} />
            <Typography sx={{ fontSize: 13 }}>
              {timeRange(reservation.startTime, reservation.endTime, timeZone)}
            </Typography>
          </Stack>
        </Box>

        {/* Actions — Upcoming only */}
        {actionable && (
          <Stack direction="row" sx={{
            gap: 1
          }}>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              startIcon={<Icon name="edit_calendar" />}
              onClick={onReschedule}
            >
              Reschedule
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<Icon name="cancel" />}
              onClick={onCancel}
            >
              {reservation.status === 'unconfirmed' ? 'Decline request' : 'Cancel'}
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
